import { colors } from 'theme';
import { useField } from 'formik';
import { Chip, Autocomplete } from '@mui/material';
import { useMemo, useState, useEffect, SyntheticEvent } from 'react';

import { controlError } from 'utils';
import { Input } from 'components/input';
import { useLazySearchQuery } from 'store/api/reference-items';
import {
  CsvLookupFormField,
  CsvLookupOptionValue,
  CsvLookupStoredValue,
} from 'types';

const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_QUERY_LENGTH = 2;
const SEARCH_RESULT_LIMIT = 20;

type Option = CsvLookupOptionValue;

const optionFromStored = (entry: CsvLookupStoredValue): Option => ({
  id: entry.referenceItemId,
  searchValue: entry.searchValue,
  displayValue: entry.displayValue,
});

const optionFromApi = (entry: {
  id: string;
  searchValue: string;
  displayValue: string;
}): Option => ({
  id: entry.id,
  searchValue: entry.searchValue,
  displayValue: entry.displayValue,
});

type FormCsvLookupFieldProps = CsvLookupFormField & {
  formId: string;
};

export const FormCsvLookupField = ({
  id,
  label,
  formId,
  placeholder,
}: FormCsvLookupFieldProps) => {
  const [field, meta, helpers] =
    useField<Array<Option | CsvLookupStoredValue>>(id);
  const [inputValue, setInputValue] = useState('');
  const [
    searchReferenceItems,
    { isError, isFetching, currentData, originalArgs },
  ] = useLazySearchQuery();

  const selected = useMemo(
    () =>
      Array.isArray(field.value)
        ? field.value.map((entry) =>
            'id' in entry ? entry : optionFromStored(entry)
          )
        : [],
    [field.value]
  );

  const query = inputValue.trim();
  const hasSearchQuery = query.length >= MIN_SEARCH_QUERY_LENGTH;
  const isCurrentQuery =
    originalArgs?.formId === formId &&
    originalArgs?.fieldId === id &&
    originalArgs?.search === query;
  const results = isCurrentQuery ? (currentData ?? []).map(optionFromApi) : [];
  const loading = hasSearchQuery && (!isCurrentQuery || isFetching);
  const searchError =
    isCurrentQuery && isError ? 'Unable to load matching records' : undefined;
  const errorText = controlError(meta, id, label);

  useEffect(() => {
    if (!hasSearchQuery) return;

    let request: ReturnType<typeof searchReferenceItems> | undefined;
    const timer = window.setTimeout(() => {
      request = searchReferenceItems({
        formId,
        fieldId: id,
        search: query,
        limit: SEARCH_RESULT_LIMIT,
      });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      request?.abort();
    };
  }, [formId, hasSearchQuery, id, query, searchReferenceItems]);

  const handleSelectionChange = (_: SyntheticEvent, value: Option[]) => {
    setInputValue('');
    helpers.setValue(value);
  };

  const removeSelected = (idToRemove: string) => {
    helpers.setValue(selected.filter((option) => option.id !== idToRemove));
  };

  return (
    <div className="flex flex-col gap-2">
      <Autocomplete
        multiple
        fullWidth
        value={selected}
        loading={loading}
        filterSelectedOptions
        inputValue={inputValue}
        renderValue={() => null}
        onChange={handleSelectionChange}
        filterOptions={(items) => items}
        options={hasSearchQuery ? results : []}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        getOptionLabel={(option) => option.displayValue}
        onInputChange={(_, value) => {
          setInputValue(value);
        }}
        noOptionsText={
          hasSearchQuery ? 'No options' : 'Type at least 2 characters to search'
        }
        sx={{
          '& .MuiAutocomplete-endAdornment': {
            right: 12,
            top: '50%',
            transform: 'translateY(-50%)',
          },
        }}
        slotProps={{
          paper: {
            sx: {
              p: 0,
              background: colors.white.primary,
              border: `1px solid ${colors.gray.primary}`,
            },
          },
        }}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;

          return (
            <li key={key} {...optionProps}>
              <span style={{ whiteSpace: 'pre-line' }}>
                {option.searchValue}
              </span>
            </li>
          );
        }}
        renderInput={({ slotProps, ...params }) => (
          <Input
            {...params}
            name={id}
            label={label}
            onBlur={field.onBlur}
            slotProps={slotProps}
            placeholder={placeholder}
            helperText={errorText || searchError}
            error={Boolean(errorText) || Boolean(searchError)}
          />
        )}
      />

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selected.map((option) => (
            <Chip
              key={option.id}
              label={option.displayValue}
              onDelete={() => removeSelected(option.id)}
              sx={{
                maxWidth: '100%',
                color: 'rgb(var(--accent-shadow-rgb))',
                backgroundColor: 'rgba(var(--accent-shadow-rgb), 0.15)',
                '&:hover': {
                  backgroundColor: 'rgba(var(--accent-shadow-rgb), 0.2)',
                },
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
                '& .MuiChip-deleteIcon': {
                  color: 'rgb(var(--accent-shadow-rgb))',
                  '&:hover': {
                    color: 'rgb(var(--accent-shadow-rgb))',
                  },
                },
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};
